import {mapObject, groupBy} from '@benchling/js-utils';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import * as ProcessManagementContextMenu from 'processes/dashboard/ProcessManagementContextMenu';
import {outputHasCreationEntry} from 'processes/dashboard/ProcessManagementSidepanelConstants';
import RequestV2SubmissionChip from 'processes/requests_v2/chips/RequestV2SubmissionChip';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import {ChipSize} from 'ui-library/chips/Chip';
import EntryChip from 'ui-library/chips/EntryChip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {BasicEvent} from 'util/Types';

import {ProcessOutputListingGraphQL} from '../../../../../../graphql-fragments/processes/__generated/ProcessOutputListingGraphQL';

export interface ProcessOutputListingExtras {}

export default class ProcessOutputListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.PROCESS_OUTPUT> {
  listingRendererType = ListingRendererType.PROCESS_OUTPUT;

  renderIcon(): JSX.Element {
    return <BenchlingIcon icon="process-output" className="scrollableTable-cell-icon" />;
  }

  getNonPluginColumnConfigurations(
    items: Array<ProcessOutputListingGraphQL>
  ): Array<ColumnConfiguration<ProcessOutputListingGraphQL>> {
    const outputCountsByExecutionType = mapObject(
      groupBy(items, ({schema}) => schema.processExecutionType),
      (outputs) => outputs.length
    );

    const shouldDisplayRequestV2SubmissionColumn =
      isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_DISCOVER}) &&
      items.some((item) => item.process.requestV2Submission != null);

    let columns: Array<ColumnConfiguration<ProcessOutputListingGraphQL>> = [
      {
        column: {
          accessorKey: ColumnAccessorKey.ID,
          header: 'ID',
          isSortable: true,
          sortKey: SortKey.SORT_IDENTIFIER_KEY,
          flexGrow: 1,
          width: 200,
        },
        render: (item) => item.humanId,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.SCHEMA,
          header: 'Schema',
          isSortable: true,
          sortKey: SortKey.SORT_SCHEMA_NAME,
          flexGrow: 1,
        },
        render: (item) => item.task.schema.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.PROCESS_ID,
          header: 'Task group ID',
          isSortable: true,
          sortKey: SortKey.SORT_PROCESS_ID,
          flexGrow: 1,
        },
        render: (item) => item.process.humanId,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.PROCESS_NAME,
          header: 'Task group name',
          flexGrow: 1,
          isSortable: false,
        },
        render: (item) => item.process.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.PROCESS_TASK_ID,
          header: 'Task ID',
          isSortable: true,
          sortKey: SortKey.SORT_PROCESS_TASK_ID,
          flexGrow: 1,
        },
        render: (item) => item.task.humanId,
      },
      ...((outputCountsByExecutionType[ProcessExecutionType.ENTRY] ?? 0) > 0
        ? [
            {
              column: {
                accessorKey: ColumnAccessorKey.ORIGIN_ENTRY,
                header: 'Entry',
                flexGrow: 1,
                isSortable: false,
              },
              render: (item: ProcessOutputListingGraphQL) =>
                outputHasCreationEntry(item) ? (
                  <EntryChip
                    onClick={(e, chipItem) => {
                      e.stopPropagation();
                      FileBrowserActions.openFile(chipItem.id);
                      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_EXECUTION_ENTRY_CHIP, {
                        process_client_origin: item.task.schema.clientOrigin,
                      });
                    }}
                    id={item.creationOrigin.origin.id}
                    size={ChipSize.TABLE_CELL}
                  />
                ) : (
                  <div />
                ),
            },
          ]
        : []),
      ...(shouldDisplayRequestV2SubmissionColumn
        ? [
            {
              column: {
                accessorKey: ColumnAccessorKey.REQUEST_V2_SUBMISSION,
                header: 'Request',
                flexGrow: 1,
                isSortable: false,
              },
              render: (item: ProcessOutputListingGraphQL) => {
                if (item.process.requestV2Submission != null) {
                  return (
                    <RequestV2SubmissionChip
                      id={getAccessibleOrInaccessibleId(item.process.requestV2Submission)}
                      size={ChipSize.MINI}
                    />
                  );
                }
                return null;
              },
            },
          ]
        : []),
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATOR,
          header: 'Creator',
          isSortable: true,
          sortKey: SortKey.SORT_CREATOR_NAME,
          flexGrow: 1,
        },
        render: (item) => getPartyDisplayString(item.creator),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.modifiedAt} showTimeInCell={false} />,
      },
    ];
    const tagColumns = ExpandedTableTagUtil.listSortedTagColumns(items);
    if (tagColumns.length) {
      columns = columns.concat(
        tagColumns.map((column: ScrollableTableColumn) => ({
          column,
          render: (item: ProcessOutputListingGraphQL) => {
            const tagsByColumn = ExpandedTableTagUtil.mapTagsToColumns({
              tags: item.fields,
              tagColumns: [column],
            });
            return tagsByColumn.tagFields[column.accessorKey];
          },
        }))
      );
    }

    return columns;
  }

  getNonPluginRowContentHeight(item: ProcessOutputListingGraphQL): number {
    return ExpandedTableTagUtil.getRowHeight(item.fields);
  }

  openContextMenu(item: ProcessOutputListingGraphQL, e: BasicEvent, extras: unknown): void {
    ProcessManagementContextMenu.open(e.nativeEvent, ModelTypeConstants.PROCESS_OUTPUT, item);
  }
}
